import * as vp from 'vopay';
import { Box, Select, DateRangePicker } from 'vopay';
import { Button } from 'UI';
import React, { useEffect, useState, FunctionComponent } from 'react';
import moment from 'moment';
import styles from './InvoicesForm.module.scss';

import { usePartnerInvoices } from '@/providers/PartnerInvoices.provider';
import usePartnerInvoicesDialogue from '@/hooks/useDialogues/usePartnerInvoices.dialogue';
import { useTranslation } from 'react-i18next';

type InvoicesFormTypes = {
  type: string;
  search: (values: PartnerInvoicesRequestType) => void;
};

type PartnerInvoicesRequestType = {
  fromDate: string;
  toDate: string;
  currency: vp.SupportedCurrencies | 'all' | '';
};

type FC = FunctionComponent<InvoicesFormTypes>;

const InvoicesForm: FC = ({ type, search }): React.JSX.Element => {
  const { response, refreshPartnerInvoices } = usePartnerInvoices();
  const { showDialogue } = usePartnerInvoicesDialogue(response);
  const [areDatesValid, setAreDatesValid] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Invoices.Form' });

  const [currency, setCurrency] = useState<vp.SupportedCurrencies | 'all' | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);

  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp] | undefined>();
  const intialTimeRange: [vp.Timestamp, vp.Timestamp] = [
    moment().subtract(1, 'week').valueOf() as vp.Timestamp,
    moment().valueOf() as vp.Timestamp,
  ];

  const currencyOptions = [
    { label: t('currencyAllOption'), value: 'all' },
    ...Object.entries(vp.CURRENCY_SELECT_OPTIONS).map(([label, value]) => ({ label, value })),
  ];

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrency(event.currentTarget.value as vp.SupportedCurrencies);
  };

  const handleSubmit = () => {
    if (!hasInvalidEndDate) {
      search({ fromDate: startDate, toDate: endDate, currency });
      refreshPartnerInvoices({ fromDate: startDate, toDate: endDate, currency: currency === 'all' ? '' : currency });
    } else {
      setShouldShowEndDateError(true);
    }
  };

  useEffect(() => {
    setAreDatesValid(isDateValid(startDate) && isDateValid(endDate));
  }, [startDate, endDate]);

  useEffect(() => {
    if (response?.errorMessage) return showDialogue();
    return () => {};
  }, [response]);

  useEffect(() => {
    const ToDate = moment().format('YYYY-MM-DD');
    const FromDate = moment().subtract(1, 'week').format('YYYY-MM-DD');
    setStartDate(FromDate);
    setEndDate(ToDate);
  }, []);

  useEffect(() => {
    if (timeRange) {
      const FromDate = moment(timeRange?.[0]).format('YYYY-MM-DD');
      const ToDate = moment(timeRange?.[1]).format('YYYY-MM-DD');
      setStartDate(FromDate);
      setEndDate(ToDate);
    }
  }, [timeRange]);

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  return (
    <Box flx="row" ai="center" gap="md">
      <Box>
        <div className={styles.datePickerContainer}>
          <DateRangePicker
            triggerVariant="outlineShadow"
            onChange={setTimeRange}
            initialTime={intialTimeRange}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
          />
        </div>
        <Select
          version="2"
          options={currencyOptions}
          placeholder={t('currencyPlaceholder')}
          name="invoice-month-select"
          value={currency}
          onBlur={() => {}}
          onChange={handleCurrencyChange}
          sort={false}
        />
      </Box>
      <Button
        id="display-statement-invoice-period-currency"
        size="small"
        colorScheme="primary"
        onClick={handleSubmit}
        disabled={!areDatesValid}
      >
        {t('submitButton', { type })}
      </Button>
    </Box>
  );
  function isDateValid(date: string) {
    if (!date) return true;

    const [, mm, dd] = date.split('-');

    const m = parseInt(mm, 10);
    const d = parseInt(dd, 10);

    const isValidMonth = m > 0 && m <= 12;
    const isValidDay = d > 0 && d <= 31 && dd.length === 2;

    return isValidMonth && isValidDay;
  }
};

export default InvoicesForm;
